// noinspection SpellCheckingInspection
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { PaginationHeaders } from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import NumberBadge from "_shared/_views/badges/number-badge";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  type MRT_TableInstance,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_Updater,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import {
  getLastSuccessfulWorkAgreementQueryByInterface,
  getWorkAgreementQueryByInterface,
  getWorkAgreementsByQuery,
  isWorkAgreementLoadIssue,
  updateWorkAgreementInterfaceQuery,
  WorkAgreementLoadIssue
} from "work/work-agreements/_store/work-agreement-redux-slice";
import { PageTab } from "work/work-agreements/_views/work-agreements-page-tabs";
import WorkAgreement from "work/work-agreements/work-agreement";
import { WorkAgreementOrderField } from "../_api/queries/_fields/work-agreement-order-field";
import {
  useGetActiveWorkAgreementsCountQuery,
  useGetArchivedWorkAgreementsCountQuery,
  useGetWorkAgreementsQuery,
} from "work/work-agreements/_api/work-agreement-api";
import { skipToken } from "@reduxjs/toolkit/query";

const RowTitle = styled(Typography)(() => ({
  display: "block",
  fontSize: "1.1em",
  fontWeight: "600",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const EllipsisCell = styled(Typography)(() => ({
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flex: "1 1 0",
  flexDirection: "column",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  minWidth: 0,
  width: "100%",
}));
const TableTitleContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
}));
const WorkAgreementTabs = styled(Tabs)(() => ({
  minWidth: 0,
  maxWidth: "100%",
  "& .MuiTabs-scroller": {
    minWidth: 0,
  },
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
}));
const MobileToolbarButtonsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));

const columnHelper = createMRTColumnHelper<WorkAgreement | WorkAgreementLoadIssue>();
const dateColumnSize = 120;
const listPageTabs = Object.values(PageTab) as string[];

type SortingState = Array<{ id: string; desc: boolean }>;
type WorkAgreementTableSearchState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

function isPageTab(value: string | undefined): value is PageTab {
  return value !== undefined && listPageTabs.includes(value);
}

export default function WorkAgreementsPage() {
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const isMdDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isXlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));

  const pageTab = isPageTab(pageTabParam) ? pageTabParam : undefined;
  const effectivePageTab = pageTab ?? PageTab.Active;
  const interfaceName = `workAgreementsPage:${effectivePageTab}`;

  const workAgreementsQuery = useSelector(getWorkAgreementQueryByInterface(interfaceName));

  const paginatedWorkAgreementsJson = useSelector(getWorkAgreementsByQuery(workAgreementsQuery));
  const paginatedWorkAgreements = useMemo(() => {
    if (!paginatedWorkAgreementsJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const workAgreementJson of paginatedWorkAgreementsJson.data) {
      try {
        if (isWorkAgreementLoadIssue(workAgreementJson)) {
          deserializedData.push(workAgreementJson);
        } else {
          const workAgreement = WorkAgreement.fromJSON(workAgreementJson);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("WorkAgreement does not have an id", workAgreementJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading WorkAgreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in workAgreementJson ? (Guid.fromJSON(workAgreementJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading WorkAgreement." });
        console.error("Error deserializing WorkAgreement JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedWorkAgreementsJson]);

  const workAgreementsAPIQuery = useGetWorkAgreementsQuery(
    workAgreementsQuery ? { query: workAgreementsQuery, interfaceName } : skipToken
  );
  const activeWorkAgreementCountQuery = useGetActiveWorkAgreementsCountQuery({ session });
  const archivedWorkAgreementCountQuery = useGetArchivedWorkAgreementsCountQuery({ session });

  // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs
  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulWorkAgreementQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((WorkAgreement | WorkAgreementLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isWorkAgreementLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const workAgreement = WorkAgreement.fromJSON(item);
          if (workAgreement?.id) {
            deserializedData.push(workAgreement as WorkAgreement & { id: Guid });
          } else {
            console.error("Work Agreement does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Work Agreement." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Work Agreement." });
        console.error("Error deserializing Work Agreement JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson]);

  const tableWorkAgreements = paginatedWorkAgreements ?? lastSuccessfulQueryResponse;
  const tableWorkAgreementsHeaders = paginatedWorkAgreementsJson?.headers ?? lastSuccessfulQueryResponseJson?.headers;

  const globalFilter = useMemo(() => getGlobalFilterFromSearchParams(searchParams), [searchParams]);
  const columnFilters = useMemo(() => getColumnFiltersFromSearchParams(searchParams), [searchParams]);
  const sorting = useMemo(() => getSortingFromSearchParams(searchParams), [searchParams]);
  const pagination = useMemo(() => getPaginationFromSearchParams(searchParams), [searchParams]);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (pageTabParam && pageTab) return;
    navigate(`/work/${PageTab.Active}`, { replace: true });
  }, [navigate, pageTab, pageTabParam]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    const filterParameters = WorkAgreementFilterParameters.fromSearchParams({
      searchParams,
      overrides: {
        isArchived: effectivePageTab === PageTab.Archived
      }
    });
    const orderParameters = WorkAgreementOrderParameters.fromSearchParams(searchParams);

    const newQuery = WorkAgreementQuery.create({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (workAgreementsQuery && newQuery.isEqualTo(workAgreementsQuery)) return;
    dispatch(
      updateWorkAgreementInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [effectivePageTab, session.user?.legalEntityId.value, session, searchParams, workAgreementsQuery, dispatch, interfaceName]);

  const updateSearchParamsFromTableState = useCallback(
    (nextState: WorkAgreementTableSearchState) => {
      const newSearchParams = getSearchParamsFromTableState(nextState);
      if (newSearchParams.toString() === searchParams.toString()) return;
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const setGlobalFilter = useCallback(
    (updater: MRT_Updater<string>) => {
      updateSearchParamsFromTableState({
        globalFilter: applyUpdater(updater, globalFilter),
        columnFilters,
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setColumnFilters = useCallback(
    (updater: MRT_Updater<ColumnFiltersState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters: applyUpdater(updater, columnFilters),
        sorting,
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setSorting = useCallback(
    (updater: MRT_Updater<Array<{ id: string; desc: boolean }>>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting: applyUpdater(updater, sorting),
        pagination,
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  const setPagination = useCallback(
    (updater: MRT_Updater<PaginationState>) => {
      updateSearchParamsFromTableState({
        globalFilter,
        columnFilters,
        sorting,
        pagination: applyUpdater(updater, pagination),
      });
    },
    [columnFilters, globalFilter, pagination, sorting, updateSearchParamsFromTableState],
  );

  useEffect(() => {
    if (workAgreementsAPIQuery.isError) {
      enqueueSnackbar("Error loading work agreement table", { variant: "error" });
    }
  }, [workAgreementsAPIQuery.isError]);

  const columns = useMemo(() => {
    const returnedColumns = [
      columnHelper.accessor(getWorkAgreementNameFilterValue, {
        header: "Name",
        id: "name",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ row, table }) => renderNameColumn(table, row.original, effectivePageTab)
      }),
      columnHelper.accessor(getWorkAgreementDescriptionFilterValue, {
        header: "Description",
        id: "description",
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ row, table }) => renderDescriptionColumn(table, row.original)
      }),
      columnHelper.accessor(getWorkAgreementHireDateSortValue, {
        header: "Hire Date",
        id: "hireDate",
        enableSorting: false,
        enableColumnFilter: false,
        minSize: dateColumnSize,
        size: dateColumnSize,
        Cell: HireDateCell
      }),
      columnHelper.accessor(getWorkAgreementStartDateSortValue, {
        header: "Start Date",
        id: "start",
        enableSorting: true,
        enableColumnFilter: false,
        minSize: dateColumnSize,
        size: dateColumnSize,
        Cell: StartDateCell
      }),
      columnHelper.accessor(getWorkAgreementEndDateSortValue, {
        header: "End Date",
        id: "end",
        enableSorting: true,
        enableColumnFilter: false,
        minSize: dateColumnSize,
        size: dateColumnSize,
        Cell: EndDateCell
      }),
      columnHelper.accessor(getWorkAgreementDiscountSortValue, {
        header: "Discount",
        id: "discount",
        enableSorting: true,
        enableColumnFilter: false,
        minSize: 100,
        size: 100,
        Cell: DiscountCell,
      }),
    ];
    if (effectivePageTab === PageTab.Archived) {
      returnedColumns.push(
        columnHelper.accessor(getWorkAgreementArchivedDateSortValue, {
          header: "Archive Date",
          id: "archived",
          enableSorting: true,
          enableColumnFilter: false,
          minSize: dateColumnSize,
          size: dateColumnSize,
          Cell: ArchiveDateCell
        }),
      );
    }
    return returnedColumns;
  }, [effectivePageTab]);

  const openWorkAgreement = useCallback(
    (workAgreement: WorkAgreement | WorkAgreementLoadIssue) => {
      if (isWorkAgreementLoadIssue(workAgreement)) {
        console.warn("Cannot open work agreement with loading issues.");
        return;
      }
      const destination = workAgreementDestination(workAgreement, effectivePageTab);
      if (!destination) {
        console.error("Work Agreement ID is required");
        return;
      }
      navigate(destination);
    },
    [navigate, effectivePageTab],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleContainer>
            <TableTitle variant="h4">Work Agreements</TableTitle>
          </TableTitleContainer>
          <LowerTopToolbarContainer>
            <WorkAgreementTabs
              value={effectivePageTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue) => navigate(`/work/${newValue}`)}
            >
              <Tab
                value={PageTab.Active}
                label={
                  <NumberBadge
                    isLoading={activeWorkAgreementCountQuery.isLoading}
                    tooltip={`${activeWorkAgreementCountQuery.data ?? 0} active work agreement(s)`}
                    badgeCount={activeWorkAgreementCountQuery.data ?? 0}
                    color="info"
                    text={PageTab.Active}
                  />
                }
              />
              <Tab
                value={PageTab.Archived}
                label={
                  <NumberBadge
                    isLoading={archivedWorkAgreementCountQuery.isLoading}
                    tooltip={`${archivedWorkAgreementCountQuery.data ?? 0} archived work agreement(s)`}
                    badgeCount={archivedWorkAgreementCountQuery.data ?? 0}
                    text={PageTab.Archived}
                  />
                }
              />
            </WorkAgreementTabs>
          </LowerTopToolbarContainer>
          {isMdDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    [
      activeWorkAgreementCountQuery.data,
      activeWorkAgreementCountQuery.isLoading,
      archivedWorkAgreementCountQuery.data,
      archivedWorkAgreementCountQuery.isLoading,
      effectivePageTab,
      isMdDisplaySize,
      navigate,
    ],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue> }) => {
      const { table } = props;

      return (
        <>
          {!isMdDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </>
      );
    },
    [isMdDisplaySize],
  );

  const isPreparingWorkAgreementsQuery =
    !sessionPermissions || (Boolean(session.user?.legalEntityId) && !workAgreementsQuery);
  const isTableLoading = isPreparingWorkAgreementsQuery || workAgreementsAPIQuery.isFetching;
  const showTableSkeletons = !tableWorkAgreements && isTableLoading;
  const skeletonWorkAgreements = useMemo(
    () =>
      Array.from({ length: pagination.pageSize }, (_, index) => ({
        id: Guid.generate(),
        issue: `Loading work agreement ${index}`,
      })),
    [pagination.pageSize],
  );
  const tableData = useMemo(
    () => (showTableSkeletons ? skeletonWorkAgreements : (tableWorkAgreements ?? [])),
    [showTableSkeletons, skeletonWorkAgreements, tableWorkAgreements],
  );
  const tableRowCount = getTableRowCount({
    headers: tableWorkAgreementsHeaders,
    isShowingSkeletons: showTableSkeletons,
    pageSize: pagination.pageSize,
    tableWorkAgreementCount: tableWorkAgreements?.length,
  });

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<WorkAgreement | WorkAgreementLoadIssue> }) => ({
      onClick: showTableSkeletons ? undefined : () => openWorkAgreement(row.original),
      sx: {
        backgroundColor: !showTableSkeletons && isWorkAgreementLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: showTableSkeletons || isWorkAgreementLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openWorkAgreement, showTableSkeletons],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (workAgreementsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [workAgreementsAPIQuery.isError]);

  const tableOptions = useMemo<MRT_TableOptions<WorkAgreement | WorkAgreementLoadIssue>>(() => {
    return {
      columns,
      data: tableData,
      // Revise, Leave Team and End Work Agreement all live in the work agreement itself, which the row opens.
      enableRowActions: false,
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      enableColumnPinning: true,
      initialState: {
        columnOrder: ["name", "description", "hireDate", "start", "end", "discount", "archived"],
        columnVisibility: {
          discount: false,
        },
        showGlobalFilter: true,
      },
      manualFiltering: true,
      enableColumnFilters: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      layoutMode: "grid",
      muiTableContainerProps: {
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative"
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden"
        },
      },
      muiTopToolbarProps: {
        sx: {
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          "& > .MuiBox-root": {
            gap: 0,
            minWidth: 0,
            overflow: "hidden",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: `Search ${effectivePageTab.toLowerCase()}`,
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: globalFilter ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setGlobalFilter("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableRowCount,
      pageCount: Math.ceil(tableRowCount / pagination.pageSize),
      enablePagination: true,
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: workAgreementsAPIQuery.isError,
        isLoading: isTableLoading,
        onRetry: () => void workAgreementsAPIQuery.refetch(),
        subject: effectivePageTab === PageTab.Archived ? "archived work agreements" : "work agreements",
      }),
      
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      positionGlobalFilter: "none",
      state: {
        columnFilters,
        density: isXlDisplaySize ? "compact" : "comfortable",
        globalFilter,
        isLoading: isTableLoading,
        pagination,
        showAlertBanner: Boolean(workAgreementsAPIQuery.isError),
        showGlobalFilter: true,
        showProgressBars: workAgreementsAPIQuery.isFetching,
        sorting,
        showSkeletons: showTableSkeletons,
      },
    };
  }, [
    columns, 
    tableData, 
    tableRowCount, 
    muiTableBodyRowProps, 
    renderToolbarInternalActions, 
    renderTopToolbarCustomActions, 
    muiToolbarAlertBannerProps, 
    pagination, 
    columnFilters, 
    effectivePageTab,
    globalFilter, 
    isTableLoading, 
    workAgreementsAPIQuery, 
    showTableSkeletons, 
    sorting, 
    setColumnFilters,
    setGlobalFilter,
    setPagination,
    setSorting,
    isXlDisplaySize, 
  ]);

  if (!sessionPermissions) return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} />;
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return <MRTNoMemo<WorkAgreement | WorkAgreementLoadIssue> options={tableOptions} />;
}

function getGlobalFilterFromSearchParams(searchParams: URLSearchParams): string {
  return searchParams.get("query") ?? "";
}

function getColumnFiltersFromSearchParams(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  const name = searchParams.get("name");
  const description = searchParams.get("description");

  if (name) filters.push({ id: "name", value: name });
  if (description) filters.push({ id: "description", value: description });

  return filters;
}

function getSortingFromSearchParams(searchParams: URLSearchParams): SortingState {
  const orderByParam = searchParams.get("orderBy")?.toLowerCase();
  const orderBy = Object.values(WorkAgreementOrderField).find((field) => field.toLowerCase() === orderByParam);
  if (!orderBy) return [];

  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir = Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam);

  return [{
    id: orderBy.toLowerCase(),
    desc: orderDir !== OrderDirection.Ascending,
  }];
}

function getPaginationFromSearchParams(searchParams: URLSearchParams): PaginationState {
  const page = Number.parseInt(searchParams.get("page") ?? "", 10);
  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "", 10);

  return {
    pageIndex: Number.isFinite(page) && page > 0 ? page - 1 : PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : PaginationParameters.DEFAULT_PAGE_SIZE,
  };
}

function getSearchParamsFromTableState(state: WorkAgreementTableSearchState): URLSearchParams {
  const { globalFilter, columnFilters, sorting, pagination } = state;
  const newSearchParams = new URLSearchParams();
  if (globalFilter) {
    newSearchParams.set("query", globalFilter);
  }

  const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
  if (nameFilter) {
    newSearchParams.set("name", nameFilter);
  }

  const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
  if (descriptionFilter) {
    newSearchParams.set("description", descriptionFilter);
  }

  const orderingField = sorting.find((sort) =>
    Object.values(WorkAgreementOrderField)
      .map((field) => field.toLowerCase())
      .includes(sort.id.toLowerCase()),
  );

  if (orderingField) {
    const orderBy = Object.values(WorkAgreementOrderField).find(
      (field) => field.toLowerCase() === orderingField.id.toLowerCase(),
    );
    if (orderBy) newSearchParams.set("orderBy", orderBy);
    newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  const paginationPageIndex = pagination.pageIndex ?? 0;
  newSearchParams.set("page", (paginationPageIndex + 1).toString());
  const paginationPageSize = pagination.pageSize ?? 10;
  newSearchParams.set("pageSize", paginationPageSize.toString());

  return newSearchParams;
}

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

function getTableRowCount({
  headers,
  isShowingSkeletons,
  pageSize,
  tableWorkAgreementCount,
}: {
  headers?: PaginationHeaders;
  isShowingSkeletons: boolean;
  pageSize: number;
  tableWorkAgreementCount?: number;
}): number {
  if (isShowingSkeletons) return pageSize;
  return headers?.totalElements ?? tableWorkAgreementCount ?? 0;
}

function LoadIssueCell({ workAgreement }: Readonly<{ workAgreement: WorkAgreementLoadIssue }>) {
  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "4000px",
            whiteSpace: "normal",
          },
        },
      }}
      title={`Our staff has been notified. Reference id: ${workAgreement.id.value}`}
    >
      <Box component="span" sx={{ color: "error.main" }}>
        Error Loading Work Agreement
      </Box>
    </Tooltip>
  );
}

/**
 * Where a row goes, in one place, because the row and the link in its name cell have to agree — and
 * the tab the agreement is listed on is part of the route.
 */
function workAgreementDestination(
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
  pageTab: PageTab,
): string | undefined {
  if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.id) return undefined;
  return `/work/${pageTab}/view/${workAgreement.id}`;
}

function renderNameColumn(
  table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>,
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
  pageTab: PageTab,
) {
  if (table.getState().isLoading) return;
  if (isWorkAgreementLoadIssue(workAgreement)) return <LoadIssueCell workAgreement={workAgreement} />;
  if (!workAgreement.name) return <Icon>remove</Icon>;
  return (
    <RowTitle>
      <RowLink to={workAgreementDestination(workAgreement, pageTab)}>{workAgreement.name.value}</RowLink>
    </RowTitle>
  );
}

function renderDescriptionColumn(
  table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>,
  workAgreement: WorkAgreement | WorkAgreementLoadIssue,
) {
  if (table.getState().isLoading) return;
  if (isWorkAgreementLoadIssue(workAgreement) || !workAgreement.description) return <Icon>remove</Icon>;
  return <EllipsisCell variant="body2">{workAgreement.description.value}</EllipsisCell>;
}

function getWorkAgreementNameFilterValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): string {
  if (isWorkAgreementLoadIssue(workAgreement)) return "";
  return workAgreement.name?.value ?? "";
}

function getWorkAgreementDescriptionFilterValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): string {
  if (isWorkAgreementLoadIssue(workAgreement)) return "";
  return workAgreement.description?.value ?? "";
}

function getWorkAgreementHireDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.hireDate?.value ?? 0;
}

function getWorkAgreementStartDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.startDate?.value ?? 0;
}

function getWorkAgreementEndDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.endDate?.value ?? 0;
}

function getWorkAgreementArchivedDateSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return workAgreement.archiveDate?.value ?? 0;
}

function getWorkAgreementDiscountSortValue(workAgreement: WorkAgreement | WorkAgreementLoadIssue): number {
  if (isWorkAgreementLoadIssue(workAgreement)) return 0;
  return Masked.isPresentNotMasked(workAgreement.discount) ? workAgreement.discount.value : 0;
}

function StartDateCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row, table }: 
  { 
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function DiscountCell({ row, table }:
  {
    row: { original: WorkAgreement | WorkAgreementLoadIssue },
    table: MRT_TableInstance<WorkAgreement | WorkAgreementLoadIssue>
  }) {
  if (table.getState().isLoading) return;
  const value = !isWorkAgreementLoadIssue(row.original) && row.original.discount;
  return Masked.isPresentNotMasked(value) ? value.toString() : <Icon>remove</Icon>;
}
